import { useState, useEffect } from 'react'
import {
    type ColumnDef,
    type SortingState,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'

import { Table } from '@/components'
import { useTableSorting } from '@/hooks/useTableSorting/useTableSorting'
import { usePaginationConfig } from '@/hooks/usePaginationConfig/usePaginationConfig'

type IntervalTableProps = {
    data: Array<Record<string, unknown>>
    columns: ColumnDef<unknown>[]
    sorting: SortingState
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>
}

export const IntervalTable: React.FC<IntervalTableProps> = ({
    data,
    columns,
    sorting,
    setSorting,
}) => {
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    })

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { sorting, pagination },
        onPaginationChange: setPagination,
    })

    const { onTableColumnHeaderClick, getTableColumnSorting } = useTableSorting(
        table,
        setSorting
    )

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(data.length / pagination.pageSize)
        )
        if (pagination.pageIndex > totalPages - 1) {
            setPagination((prev) => ({ ...prev, pageIndex: 0 }))
        }
    }, [data.length, pagination.pageIndex, pagination.pageSize])

    const paginationConfig = usePaginationConfig({
        totalItems: data.length,
        recordsPerPage: pagination.pageSize,
        currentPage: pagination.pageIndex + 1,
        onPageChange: (nextPage) => {
            table.setPageIndex(nextPage - 1)
        },
        onRecordsPerPageChange: (value) => {
            const size = parseInt(value as string, 10)
            table.setPageSize(size)
            table.setPageIndex(0)
        },
        siblingCount: 1,
        boundaryCount: 1,
    })

    return (
        <div className="h-full">
            <div className="absolute inset-0 h-full w-full overflow-auto">
                <Table
                    table={table}
                    onTableColumnHeaderClick={onTableColumnHeaderClick}
                    getColumnSorting={getTableColumnSorting}
                    pagination={paginationConfig}
                />
            </div>
        </div>
    )
}
